import { Buffer } from 'buffer';

import { SkpAlert, SkpLoader } from '@skeepers/skeepers-ui-react';
import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Routes, Route, BrowserRouter } from 'react-router-dom';

import { signIn } from '@app/auth';
import { SequencesProvider } from '@app/contexts/SequencesContext';
import { UploadProvider } from '@app/contexts/UploadProvider';
import { WindowProvider } from '@app/contexts/WindowContext';
import ScrollToTop from '@app/ScrollToTop';
import { hexColorToRgba, setCssVariables } from '@app/utils/styles';
import AppContainer from '@components/AppContainer';
import CrossCancel from '@components/CrossCancel';
import SupportWidget from '@components/SupportWidget';
import useGetBrief from '@hooks/useGetBrief';
import useGetConnectedUser from '@hooks/useGetMe';

import { loadExternalModules } from './utils/externalModules';
import { cleanStorageInformation } from './utils/localstorage';
import { briefRoute, sequenceRoute, getQueryParams } from './utils/routing';

// temporary fix for ts-ebml package that is used inside @skeepers/capture-core
window.Buffer = Buffer;

const App = ({ t = () => {} }) => {
  // By default all call don't need authentification
  localStorage.setItem('SKP-CRED', 'omit');

  const { solutionId, allowResume, parameters } = getQueryParams(window.location.search);
  const { brief, loading: briefLoading } =  useGetBrief(solutionId);

  // If the project is not EMBED we need to ask for auth to the API
  if (!briefLoading && brief && (brief.config.policy !== 'EMBED' || brief.config.auth)) {
    localStorage.setItem('SKP-CRED', 'include');
  }

  const primaryColor = brief?.config?.color || '#3271f5';
  const { connectedUser, loading: loadingUser } = useGetConnectedUser(briefLoading);

  useEffect(() => {
    if (brief && brief.config.policy !== 'EMBED') {
      cleanStorageInformation();
    }
  }, [brief]);

  useEffect(() => {
    loadExternalModules();
  }, []);

  useLayoutEffect(() => {
    setCssVariables([
      ['--skp-color-primary-500', primaryColor],
      ['--skp-color-primary-400', hexColorToRgba(primaryColor, 0.9)],
      ['--skp-color-primary-50', hexColorToRgba(primaryColor, 0.05)]
    ]);
  }, [primaryColor]);

  if (briefLoading || loadingUser) {
    return (
      <div className='loaderContainer'>
        <SkpLoader size='medium' />
        <p>{t('Loading...')}
        </p>
      </div>
    )
  }

  if (!brief) {
    return (
      <div className='errorLoading'>
        <SkpAlert
          data-testid="error-alert"
          closable={false}
          size='large'
          text={t("Please don't use a given link, use the button received in email to start or continue your participation")}
          type='error'
        />
      </div>
    );
  }

  const withAuth = brief.config.auth;

  if (withAuth && !loadingUser && !connectedUser) {
    signIn();
    return null;
  }

  const routes = [
    {
      path: briefRoute(),
      View: lazy(() => import('@views/SequenceBrief/SequenceBrief')),
      props: {
        mustDoSequenceInOrder: brief?.config?.inOrder || false,
        productInfos: parameters,
        participationMeta: parameters.meta ? JSON.stringify(parameters.meta) : null,
        withAuth,
      },
    },
    {
      path: sequenceRoute(),
      View: lazy(() => import('@views/Brief/Brief')),
      props: {
        productInfos: parameters,
      },
    },
    {
      path: `${sequenceRoute()}/video`,
      View: lazy(() => import('@views/captures/VideoCapture')),
      showZendeskWidget: false,
    },
    {
      path: `${sequenceRoute()}/audio`,
      View: lazy(() => import('@views/captures/AudioCapture')),
    },
    {
      path: `${sequenceRoute()}/video/validation`,
      View: lazy(() => import('@views/validations/VideoValidation/VideoValidation')),
      props: {
        productInfos: parameters,
      },
    },
    {
      path: `${sequenceRoute()}/audio/validation`,
      View: lazy(() => import('@views/validations/AudioValidation/AudioValidation')),
    },
    {
      path: `${sequenceRoute()}/upload`,
      View: lazy(() => import('@views/Upload/Upload')),
      props: {
        withAuth,
      },
    },
    {
      path: '/thanks',
      View: lazy(() => import('@views/Thanks/Thanks')),
      props: {
        successCallback: parameters.callbacks?.success,
      },
    },
    {
      path: '/',
      View: lazy(() => import('@views/CGUValidation/CGUValidation')),
      props: {
        allowResume: allowResume,
        participationForward: parameters.forward ? JSON.stringify(parameters.forward) : null,
        participationMeta: parameters.meta ? JSON.stringify(parameters.meta) : null,
        withAuth,
        productInfos: parameters,
        config: brief?.config,
      },
      showZendeskWidget: false,
    },
  ];

  return (
    <AppContainer primaryColor={primaryColor}>
      <BrowserRouter>
        <Suspense fallback={<div className='loaderContainer'><SkpLoader size='medium'/><p>{t('Loading...')}</p></div>}>
          <SequencesProvider brief={brief}>
            <WindowProvider>
              <UploadProvider brief={brief}>
                <ScrollToTop />
                <CrossCancel cancelCallback={parameters.callbacks?.cancel} />
                <Routes>
                  {routes.map(({ path, View, props = {}, showZendeskWidget = true }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          <View {...props} />
                          {showZendeskWidget && <SupportWidget connectedUser={connectedUser} brief={brief} />}
                        </>
                      }
                    />
                  ))}
                </Routes>
              </UploadProvider>
            </WindowProvider>
          </SequencesProvider>
        </Suspense>
      </BrowserRouter>
    </AppContainer>
  );
};

export default withTranslation()(App);
